import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { handleStructuredPaste } from '@/lib/pasteUtils';
import { toast } from 'sonner';
import { 
  Plus, 
  Clock,
  Calendar,
  Briefcase,
  Trash2,
  Loader2,
  Pencil
} from 'lucide-react';

const TimeTrackingPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [timeEntries, setTimeEntries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterJobId, setFilterJobId] = useState('all');
  const [editingEntry, setEditingEntry] = useState(null);
  
  const [newEntry, setNewEntry] = useState({
    job_id: '',
    hours: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  const fetchData = async () => {
    try {
      const [entriesRes, jobsRes] = await Promise.all([
        axios.get(`${API}/time-entries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTimeEntries(entriesRes.data);
      setJobs(jobsRes.data.filter(j => j.status !== 'completed' && j.status !== 'cancelled'));
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleAddEntry = async () => {
    if (!newEntry.job_id || !newEntry.hours) {
      toast.error('Please select a job and enter hours');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/time-entries`, {
        ...newEntry,
        hours: parseFloat(newEntry.hours)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Time entry added');
      setDialogOpen(false);
      setNewEntry({ job_id: '', hours: '', description: '', date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (err) {
      toast.error('Failed to add time entry');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (entry) => {
    setEditingEntry({ ...entry });
    setEditDialogOpen(true);
  };

  const handleUpdateEntry = async () => {
    if (!editingEntry) return;
    
    // Note: Backend doesn't have update endpoint for time entries yet
    // For now, we'll delete and recreate
    setSaving(true);
    try {
      await axios.delete(`${API}/time-entries/${editingEntry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await axios.post(`${API}/time-entries`, {
        job_id: editingEntry.job_id,
        hours: parseFloat(editingEntry.hours),
        description: editingEntry.description,
        date: editingEntry.date
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Time entry updated');
      setEditDialogOpen(false);
      setEditingEntry(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update time entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await axios.delete(`${API}/time-entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Time entry deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete time entry');
    }
  };

  const filteredEntries = filterJobId === 'all' 
    ? timeEntries 
    : timeEntries.filter(e => e.job_id === filterJobId);

  const totalHours = filteredEntries.reduce((sum, e) => sum + e.hours, 0);

  // Group entries by date
  const groupedEntries = filteredEntries.reduce((groups, entry) => {
    const date = entry.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(entry);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedEntries).sort((a, b) => new Date(b) - new Date(a));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Time Tracking</h1>
          <p className="text-slate-500 mt-1">Log and manage your work hours</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1F3C4F] hover:bg-[#152a38]" data-testid="log-time-btn">
              <Plus className="w-4 h-4 mr-2" />
              Log Time
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Log Time</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Job *</Label>
                <Select value={newEntry.job_id} onValueChange={(value) => setNewEntry({ ...newEntry, job_id: value })}>
                  <SelectTrigger data-testid="time-job-select">
                    <SelectValue placeholder="Select a job" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        <span className="font-mono text-[#1F3C4F] mr-2">{job.job_number}</span>
                        {job.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hours">Hours *</Label>
                  <Input
                    id="hours"
                    type="number"
                    step="0.5"
                    min="0.5"
                    placeholder="0.0"
                    value={newEntry.hours}
                    onChange={(e) => setNewEntry({ ...newEntry, hours: e.target.value })}
                    data-testid="time-hours-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={newEntry.date}
                    onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="What did you work on?"
                  value={newEntry.description}
                  onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                  onPaste={handleStructuredPaste}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddEntry} disabled={saving} className="bg-[#1F3C4F] hover:bg-[#152a38]" data-testid="time-submit-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Log Time
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingEntry(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Time Entry</DialogTitle>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Job</Label>
                <Select value={editingEntry.job_id} onValueChange={(value) => setEditingEntry({ ...editingEntry, job_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a job" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        <span className="font-mono text-[#1F3C4F] mr-2">{job.job_number}</span>
                        {job.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-hours">Hours</Label>
                  <Input
                    id="edit-hours"
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={editingEntry.hours}
                    onChange={(e) => setEditingEntry({ ...editingEntry, hours: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-date">Date</Label>
                  <Input
                    id="edit-date"
                    type="date"
                    value={editingEntry.date}
                    onChange={(e) => setEditingEntry({ ...editingEntry, date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  placeholder="What did you work on?"
                  value={editingEntry.description || ''}
                  onChange={(e) => setEditingEntry({ ...editingEntry, description: e.target.value })}
                  onPaste={handleStructuredPaste}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateEntry} disabled={saving} className="bg-[#1F3C4F] hover:bg-[#152a38]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-[#1F3C4F] to-[#152a38]">
          <CardContent className="p-6">
            <div className="text-white">
              <p className="text-white/70 text-sm">Total Hours</p>
              <p className="text-4xl font-heading font-bold mt-2">{totalHours.toFixed(1)}</p>
              <p className="text-white/70 text-sm mt-1">{filteredEntries.length} entries</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <Label className="text-sm text-slate-500">Filter by Job</Label>
            <Select value={filterJobId} onValueChange={setFilterJobId}>
              <SelectTrigger className="mt-2" data-testid="filter-job-select">
                <SelectValue placeholder="All Jobs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jobs</SelectItem>
                {jobs.map((job) => (
                  <SelectItem key={job.id} value={job.id}>
                    {job.job_number} - {job.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Time Entries by Date - Clickable Cards */}
      {sortedDates.length > 0 ? (
        <div className="space-y-6" data-testid="time-entries-list">
          {sortedDates.map((date) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-5 h-5 text-slate-400" />
                <h2 className="font-heading font-semibold text-lg text-slate-700">
                  {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { 
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                  })}
                </h2>
                <span className="text-sm text-slate-500">
                  ({groupedEntries[date].reduce((sum, e) => sum + e.hours, 0).toFixed(1)} hrs)
                </span>
              </div>
              <div className="space-y-3">
                {groupedEntries[date].map((entry) => (
                  <Card 
                    key={entry.id} 
                    className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer"
                    onClick={() => handleOpenEdit(entry)}
                    data-testid={`time-entry-${entry.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-10 h-10 rounded-lg bg-[#1F3C4F]/10 flex items-center justify-center cursor-pointer hover:bg-[#1F3C4F]/20"
                            onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${entry.job_id}`); }}
                          >
                            <Clock className="w-5 h-5 text-[#1F3C4F]" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span 
                                className="font-mono text-sm text-[#1F3C4F] cursor-pointer hover:underline"
                                onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${entry.job_id}`); }}
                              >
                                {entry.job_number}
                              </span>
                            </div>
                            <p className="font-medium text-slate-900">{entry.description || 'No description'}</p>
                            <p className="text-sm text-slate-500">{entry.user_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-2xl font-heading font-bold text-[#1F3C4F]">{entry.hours}</p>
                            <p className="text-sm text-slate-500">hours</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleOpenEdit(entry); }}
                            className="text-slate-400 hover:text-[#1F3C4F]"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Clock className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="font-heading font-semibold text-lg text-slate-700">No time entries yet</h3>
            <p className="text-slate-500 mt-1">Start logging your work hours</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TimeTrackingPage;
